import { useCallback, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app";
import {
  fetchDocumentSetsTrigger,
  removeDocumentSets,
  resetSelectedRows,
  updateSelectedRow,
} from "../store/documentSetSlice";
import http from "../../common/utilities/HttpModule";
import { notification } from "antd";
import { useTranslation } from "react-i18next";
import { ADMIN_ROLE_ID } from "../../common/utilities/env";
import { NotificationBody } from "../../app/interfaces/notification";

const useBulkAction = () => {
  const { t } = useTranslation("notifications");
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState<
    "reprocess" | "reexport" | "reapprove" | "unassign" | null
  >(null);

  const dispatch = useAppDispatch();

  const { selectedRows } = useAppSelector((state) => state.documentSetsReducer);

  const user = useAppSelector((state) => state.userReducer.user);

  const toggleBulkActionCheckbox = useCallback(
    (id: string) => {
      dispatch(updateSelectedRow(id));
    },
    [dispatch],
  );

  const resetRows = useCallback(() => {
    dispatch(resetSelectedRows());
  }, [dispatch]);

  const handleAction = useCallback(
    async (
      url: string,
      selectedRows: Array<string>,
      successNotification: NotificationBody,
      errorNotification: NotificationBody,
      errorLog: string,
    ) => {
      if (!selectedRows.length) {
        return;
      }

      try {
        await http.post(url, selectedRows);

        dispatch(removeDocumentSets(selectedRows));
        resetRows();
        dispatch(fetchDocumentSetsTrigger());

        notification.success(successNotification);
      } catch {
        console.error(errorLog);

        notification.error(errorNotification);
      }
    },
    [dispatch, resetRows],
  );

  const removeRows = useCallback(async () => {
    if (!selectedRows.length) {
      return;
    }

    const url = `api/v1/document-sets/remove?assignee=${encodeURI(user!.id)}`;

    const successNotification = {
      key: "deleteToast",
      title: t("documentsRemoved.success.message"),
      description: t("documentsRemoved.success.description"),
    };
    const errorNotification = {
      key: "deleteToast",
      title: t("documentsRemoved.error.message"),
      description: t("documentsRemoved.error.description"),
    };
    const errorLog = "Failed to remove document sets";

    await handleAction(
      url,
      selectedRows,
      successNotification,
      errorNotification,
      errorLog,
    );
  }, [selectedRows, t, user, handleAction]);

  const rejectRows = useCallback(async () => {
    if (!selectedRows.length) {
      return;
    }

    const url = `api/v1/document-sets/reject?assignee=${encodeURI(user!.id)}`;

    const successNotification = {
      key: "rejectToast",
      title: t("documentsRejected.success.message"),
      description: t("documentsRejected.success.description"),
    };
    const errorNotification = {
      key: "rejectToast",
      title: t("documentsRejected.error.message"),
      description: t("documentsRejected.error.description"),
    };
    const errorLog = "Failed to reject document sets";

    await handleAction(
      url,
      selectedRows,
      successNotification,
      errorNotification,
      errorLog,
    );
  }, [selectedRows, t, user, handleAction]);

  const reExportRows = useCallback(
    async (includeAssignee: boolean, isReApprove: boolean = false) => {
      if (!selectedRows.length || isProcessing) {
        return;
      }

      // Check if user has admin access
      if (!user?.access?.includes(ADMIN_ROLE_ID)) {
        notification.error({
          key: "reExportPermissionToast",
          title: t(
            "documentsReExported.permission.message",
            "Permission denied",
          ),
          description: t(
            "documentsReExported.permission.description",
            "Only administrators can re-export documents",
          ),
        });
        return;
      }

      // Add assignee parameter if requested
      const url = includeAssignee
        ? `api/v1/document-sets/approve?assignee=${encodeURI(user!.id)}`
        : `api/v1/document-sets/approve`;

      setIsProcessing(true);
      setProcessingAction(isReApprove ? "reapprove" : "reexport");
      try {
        await http.post(url, selectedRows);

        dispatch(removeDocumentSets(selectedRows));
        resetRows();
        dispatch(fetchDocumentSetsTrigger());

        notification.success({
          key: "reExportToast",
          title: t(
            "documentsReExported.success.message",
            "Documents re-exported successfully",
          ),
          description: t(
            "documentsReExported.success.description",
            "The selected documents have been queued for re-export",
          ),
        });
      } catch {
        console.error("Failed to re-export document sets");

        notification.error({
          key: "reExportToast",
          title: t(
            "documentsReExported.error.message",
            "Failed to re-export documents",
          ),
          description: t(
            "documentsReExported.error.description",
            "An error occurred while re-exporting the documents",
          ),
        });
      } finally {
        setIsProcessing(false);
        setProcessingAction(null);
      }
    },
    [selectedRows, user, t, dispatch, resetRows, isProcessing],
  );

  const reprocessRows = useCallback(async () => {
    if (!selectedRows.length || isProcessing) {
      return;
    }

    // Check if user has admin access
    if (!user?.access?.includes(ADMIN_ROLE_ID)) {
      notification.error({
        key: "reprocessPermissionToast",
        title: t(
          "documentsReprocessed.permission.message",
          "Permission denied",
        ),
        description: t(
          "documentsReprocessed.permission.description",
          "Only administrators can reprocess documents",
        ),
      });
      return;
    }

    const url = `api/v1/document-sets/reprocess`;

    setIsProcessing(true);
    setProcessingAction("reprocess");
    try {
      await http.post(
        url,
        selectedRows.map((documentSetId) => ({ documentSetId })),
      );

      dispatch(removeDocumentSets(selectedRows));
      resetRows();
      dispatch(fetchDocumentSetsTrigger());

      notification.success({
        key: "reprocessToast",
        title: t(
          "documentsReprocessed.success.message",
          "Documents reprocessed successfully",
        ),
        description: t(
          "documentsReprocessed.success.description",
          "The selected documents have been queued for reprocessing",
        ),
      });
    } catch (error: unknown) {
      console.error("Failed to reprocess document sets", error);

      // Check if it's a 404 error indicating no reprocessing config
      const reprocessError = error as
        { response?: { status?: number } } | null | undefined;
      if (reprocessError?.response?.status === 404) {
        notification.error({
          key: "reprocessToast",
          title: t(
            "documentsReprocessed.noConfig.message",
            "Reprocessing not configured",
          ),
          description: t(
            "documentsReprocessed.noConfig.description",
            "No reprocessing configuration found for the selected documents. Please contact your administrator to set up reprocessing for this input.",
          ),
        });
      } else {
        notification.error({
          key: "reprocessToast",
          title: t(
            "documentsReprocessed.error.message",
            "Failed to reprocess documents",
          ),
          description: t(
            "documentsReprocessed.error.description",
            "An error occurred while reprocessing the documents",
          ),
        });
      }
    } finally {
      setIsProcessing(false);
      setProcessingAction(null);
    }
  }, [selectedRows, user, t, dispatch, resetRows, isProcessing]);

  const unassignRows = useCallback(async () => {
    if (!selectedRows.length || isProcessing) {
      return;
    }

    const url = `api/v1/document-sets/unassign`;

    setIsProcessing(true);
    setProcessingAction("unassign");
    try {
      await http.post(url, selectedRows);

      dispatch(fetchDocumentSetsTrigger());

      notification.success({
        key: "unassignToast",
        title: t(
          "documentsUnassigned.success.message",
          "Documents unassigned successfully",
        ),
        description: t(
          "documentsUnassigned.success.description",
          "The selected documents have been unassigned",
        ),
      });
    } catch {
      console.error("Failed to unassign document sets");

      notification.error({
        key: "unassignToast",
        title: t(
          "documentsUnassigned.error.message",
          "Failed to unassign documents",
        ),
        description: t(
          "documentsUnassigned.error.description",
          "An error occurred while unassigning the documents",
        ),
      });
    } finally {
      setIsProcessing(false);
      setProcessingAction(null);
    }
  }, [selectedRows, t, dispatch, isProcessing]);

  return {
    selectedRows,
    toggleBulkActionCheckbox,
    removeRows,
    rejectRows,
    reExportRows,
    reprocessRows,
    unassignRows,
    resetRows,
    isProcessing,
    processingAction,
  };
};

export default useBulkAction;
